import React, { useCallback, useState } from 'react'
import Popconfirm from 'antd/lib/popconfirm'
import message from 'antd/lib/message'

import { useBreakpoints, useQuery } from 'hooks'
import { apiManager, dateFormatter } from 'helpers'
import { ROUTES } from 'helpers/constants'

import Spinner from 'components/Spinner'
import Button from 'components/Button'
import Profile from 'pages/admin/ForYouProfiles/Profile'

import FreeSubscriptionModal from './FreeSubscriptionModal'
import { SUBSCRIPTION_STATUS } from 'constants/subscription'
import MatchModal from 'pages/admin/AdminUser/MatchModal'
import RecommendationsModal from 'pages/admin/AdminUser/RecommendationsModal'
import FeedbackModal from 'pages/admin/AdminUser/FeedbackModal'

const activeSubscriptionStatuses = [
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.TRIALING,
  SUBSCRIPTION_STATUS.PAUSED,
]

const AdminUser = ({ history, match }) => {
  const {
    params: { id },
  } = match
  const { md } = useBreakpoints()

  const [showFreeSubscriptionModal, setShowFreeSubscriptionModal] = useState(false)
  const [showMatchModal, setShowMatchModal] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false)
  const [isPreventSubRenewal, setIsPreventSubRenewal] = useState(false)
  const [isPausingSub, setIsPausingSub] = useState(false)
  const [isResumingSub, setIsResumingSub] = useState(false)

  const {
    data: user,
    isLoading,
    refetch: reloadUserData,
  } = useQuery(['admin/user', id], () => apiManager.adminGetUserProfile(id), {
    refetchOnWindowFocus: false,
  })

  const handleSubscriptionAction = useCallback(
    async ({ setLoader, action, successMessage }) => {
      setLoader(true)

      try {
        await action

        message.destroy()
        message.success(successMessage, 2.5)

        reloadUserData()
      } catch (error) {
        message.destroy()
        message.error(error.message || 'Please try again')
      } finally {
        setLoader(false)
      }
    },
    [reloadUserData]
  )

  const handlePreventSubRenewal = useCallback(async () => {
    await handleSubscriptionAction({
      action: apiManager.adminPreventSubscriptionRenewal({
        data: {
          userId: user.id,
        },
      }),
      successMessage: 'Renewal prevented successful',
      setLoader: setIsPreventSubRenewal,
    })
  }, [user, handleSubscriptionAction])

  const handleSubPause = useCallback(async () => {
    await handleSubscriptionAction({
      action: apiManager.adminPauseSubscription(user.id),
      successMessage: 'Subscription paused successful',
      setLoader: setIsPausingSub,
    })
  }, [user, handleSubscriptionAction])

  const handleSubResumption = useCallback(async () => {
    await handleSubscriptionAction({
      action: apiManager.adminResumeSubscription(user.id),
      successMessage: 'Subscription resumed successful',
      setLoader: setIsResumingSub,
    })
  }, [user, handleSubscriptionAction])

  const handleDeleteUser = useCallback(async () => {
    setIsPreventSubRenewal(true)

    try {
      await apiManager.adminDeleteUser(user.id)

      message.destroy()
      message.success('User deleted successfully', 2.5)

      history.push(ROUTES.ADMIN_ACTIVE_USERS)
    } catch (error) {
      message.destroy()
      message.error(error.message || 'Please try again')
    } finally {
      setIsPreventSubRenewal(false)
    }
  }, [user, history])

  return (
    <div className="flex flex-col items-center justify-center bg-white h-full w-full min-h-screen pt-4">
      {isLoading ? (
        <div className="h-72 w-full flex items-center justify-center">
          <Spinner />
        </div>
      ) : user ? (
        <div className="pb-24 px-5 max-w-5xl">
          <div className="mb-5">
            <Profile user={user} md={md} admin />
          </div>
          <div className="mb-8 w-full shadow-md text-center p-4 rounded-xl">
            <div className="text-xl text-blue-800 underline font-bold mb-1">
              Subscription Details
            </div>
            <div className="font-medium">
              <p>
                <b>Status: </b> {user.subscription?.status.toUpperCase() || 'N/A'}
              </p>
              <p>
                <b>Amount: </b>{' '}
                {user.subscription?.amount ? `USD ${user.subscription.amount / 100}` : 'N/A'}
              </p>
              <p>
                <b>Next charge at: </b>{' '}
                {user.subscription?.nextChargeAt
                  ? dateFormatter.returnYYYYMMDD(user.subscription.nextChargeAt)
                  : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex justify-center mb-3">
            <div className="w-108">
              <div className="mb-4">
                <Button
                  className="bg-transparent text-base w-full ring ring-black"
                  textColor="text-black"
                  onClick={() => setShowRecommendationsModal(true)}
                >
                  View Match Recommendations
                </Button>
              </div>
              <div className="mb-4">
                <Button
                  className="bg-transparent text-base w-full ring ring-black"
                  textColor="text-black"
                  onClick={() => setShowMatchModal(true)}
                >
                  Queue upcoming match
                </Button>
              </div>
              <div className="mb-4">
                <Button
                  className="bg-transparent text-base w-full ring ring-black"
                  textColor="text-black"
                  onClick={() => setShowFeedbackModal(true)}
                >
                  Add feedback
                </Button>
              </div>
              {user.subscription?.active &&
                activeSubscriptionStatuses.includes(user.subscription?.status) && (
                  <div className="mt-6">
                    <div className="mb-2 text-center font-semibold underline text-orange">
                      Subscriptions
                    </div>
                    <div className="mb-4">
                      {user.subscription.status === SUBSCRIPTION_STATUS.PAUSED ? (
                        <Button
                          className="bg-transparent text-base w-full ring ring-black"
                          textColor="text-black"
                          loading={isResumingSub}
                          onClick={handleSubResumption}
                        >
                          Resume Subscription
                        </Button>
                      ) : (
                        <Button
                          className="bg-transparent text-base w-full ring ring-black"
                          textColor="text-black"
                          loading={isPausingSub}
                          onClick={handleSubPause}
                        >
                          Pause Subscription
                        </Button>
                      )}
                    </div>
                    <div>
                      <Button
                        className="bg-transparent text-base w-full ring ring-black"
                        textColor="text-black"
                        onClick={() => setShowFreeSubscriptionModal(true)}
                      >
                        Add Free Months
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex space-x-4">
              <Popconfirm
                title="Prevent Sub Renewal?"
                onConfirm={handlePreventSubRenewal}
                okText="Yes"
                cancelText="No"
              >
                {user.subscription?.active &&
                  user.subscription.migrateToReplacementPlanAfterCancellation && (
                    <Button className="bg-blue-500 text-base" loading={isPreventSubRenewal}>
                      Prevent Sub Renewal
                    </Button>
                  )}
              </Popconfirm>
            </div>
            <Popconfirm
              title="Delete user (not reversible)?"
              onConfirm={handleDeleteUser}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button className="bg-red-500 text-base">Delete User</Button>
            </Popconfirm>
          </div>
        </div>
      ) : (
        <div>Not found</div>
      )}
      {user && (
        <>
          <FreeSubscriptionModal
            userId={user?.id}
            show={showFreeSubscriptionModal}
            onComplete={reloadUserData}
            onClose={() => setShowFreeSubscriptionModal(false)}
          />
          <MatchModal user={user} show={showMatchModal} onClose={() => setShowMatchModal(false)} />
          <RecommendationsModal
            user={user}
            show={showRecommendationsModal}
            onClose={() => setShowRecommendationsModal(false)}
          />
          <FeedbackModal
            user={user}
            show={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
          />
        </>
      )}
    </div>
  )
}

export default AdminUser
